import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

// Helper function to get token
const getToken = () => {
  return localStorage.getItem('access_token') || localStorage.getItem('token');
};

// Prevents multiple parallel refresh calls when several requests 401 at once
let refreshPromise = null;

// Helper function to refresh token
const refreshToken = async () => {
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) {
    return { success: false, reason: 'no_refresh_token' };
  }

  try {
    const response = await fetch('https://backend.hira-tech.co.uk/api/token/refresh/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      return { success: true, token: data.access };
    }

    // Refresh endpoint explicitly rejected the refresh token (expired/invalid/blacklisted)
    if (response.status === 401 || response.status === 400) {
      return { success: false, reason: 'refresh_invalid' };
    }

    // Some other server-side issue (5xx) — do NOT treat this as "log the user out"
    return { success: false, reason: 'server_error' };
  } catch (error) {
    // Network failure / timeout / offline — NOT an auth failure, don't nuke the session
    console.error('Refresh token network error:', error);
    return { success: false, reason: 'network_error' };
  }
};

// Ensures only one refresh call happens at a time, even if multiple requests 401 together
const getRefreshedToken = () => {
  if (!refreshPromise) {
    refreshPromise = refreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Base query with retry logic
const baseQuery = fetchBaseQuery({
  baseUrl: 'https://backend.hira-tech.co.uk/',
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

const clearSessionAndRedirect = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('token');
  window.location.href = '/login';
};

// Wrapper to handle 401 errors
const baseQueryWithRetry = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Only attempt refresh if the ORIGINAL request actually failed due to auth (401)
  if (result.error && result.error.status === 401) {
    console.log('Token expired, attempting to refresh...');
    const refreshResult = await getRefreshedToken();

    if (refreshResult.success) {
      // prepareHeaders will automatically pick up the new token from localStorage
      result = await baseQuery(args, api, extraOptions);

      // Only force logout if the retried request is ALSO an auth failure.
      // Any other error (400/500/network) should just surface normally to the UI.
      if (result.error && (result.error.status === 401 || result.error.status === 403)) {
        console.log('New token also rejected, logging out...');
        clearSessionAndRedirect();
      }
    } else if (refreshResult.reason === 'refresh_invalid') {
      // Refresh token itself is genuinely expired/invalid — this IS a real logout case
      console.log('Refresh token invalid/expired, redirecting to login...');
      clearSessionAndRedirect();
    } else {
      // Network error or server hiccup while refreshing — do NOT log the user out.
      // Let the original error bubble up so the UI can show a retry-able message.
      console.log('Could not refresh token (network/server issue) — not logging out.', refreshResult.reason);
    }
  }

  return result;
};

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: baseQueryWithRetry,
  endpoints: (builder) => ({
    getAllOrder: builder.query({
      query: () => ({
        url: 'order/getAllorder/',
        method: 'GET',
      }),
      providesTags: ['Orders'],
    }),
    updateOrder: builder.mutation({
      query: (order) => {
        const { id, ...rest } = order;
        return {
          url: `order/update/${id}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: ['Orders'],
    }),
    detaildOrder: builder.query({
      query: (id) => ({
        url: `order/getDetaildOrder/${id}`,
        method: 'GET',
      }),
      providesTags: ['OrderDetail'],
    }),
    orderedProducts: builder.query({
      query: (id) => ({
        url: `order/orderdProducts/${id}`,
        method: 'GET',
      }),
    }),
    deleteOrder: builder.mutation({
      query: (order) => ({
        url: `order/delete/${order}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Orders'],
    }),
    updateOrderTotal: builder.mutation({
      query: (order) => {
        const { id, ...rest } = order;
        return {
          url: `order/update-total/${id}/`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: ['Orders', 'OrderDetail'],
    }),
    updatePayment: builder.mutation({
      query: (order) => {
        const { id, ...rest } = order;
        return {
          url: `order/paymentUpdate/${id}`,
          method: 'PUT',
          body: rest,
        };
      },
      invalidatesTags: ['Orders'],
    }),
    placeOrder: builder.mutation({
      query: (orderData) => ({
        url: 'order/placeOrder/',
        method: 'POST',
        body: orderData,
      }),
      invalidatesTags: ['Orders'],
    }),
  }),
});

export const {
  useGetAllOrderQuery,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
  useDetaildOrderQuery,
  useOrderedProductsQuery,
  useUpdatePaymentMutation,
  useUpdateOrderTotalMutation,
  usePlaceOrderMutation,
} = orderApi;